import classNames from 'classnames'
import { Id, toast, ToastContentProps } from 'react-toastify'

import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { FileIcon } from '@/Atomic/Molecules/FileIcon'

import './UploadToast.scss'

export interface UploadToastProps extends Partial<ToastContentProps> {
  title: string
  percent: number
  extension?: string
  onClose?: () => void
}

export const UploadToast: React.FC<UploadToastProps> = ({
  title,
  percent,
  extension = 'generic',
  closeToast,
  onClose,
}) => {
  const handleClose = () => {
    onClose?.()
    closeToast?.()
  }

  const clamped = Math.max(0, Math.min(100, Math.round(percent)))

  return (
    <div className={classNames('toast show bg-light upload-toast')}>
      <div className="toast-body">
        <div className="upload-header">
          <div className="title">
            <FileIcon extension={extension} />
            <StringText variant="title" value={title} />
          </div>
          <Button
            variant="icon"
            icon="cross"
            title="close"
            onClick={handleClose}
          />
        </div>
        <div className="progress-row">
          <div className="progress-track">
            <div className="progress-fill" style={{ width: `${clamped}%` }} />
          </div>
          <StringText className="percent" value={clamped + '%'} />
        </div>
      </div>
    </div>
  )
}

// Helper API to show and update the upload toast
export type UploadToastController = {
  id: Id
  update: (next: Partial<Pick<UploadToastProps, 'title' | 'percent'>>) => void
  close: () => void
}

export function showUploadToast(
  initial: UploadToastProps,
): UploadToastController {
  const id = toast(<UploadToast {...initial} />, {
    closeOnClick: false,
    draggable: false,
    autoClose: false,
  })

  return {
    id,
    update: (next) => {
      const merged = { ...initial, ...next }
      toast.update(id, {
        render: <UploadToast {...merged} />,
      })
      // keep latest as base for future merges
      Object.assign(initial, merged)
    },
    close: () => toast.dismiss(id),
  }
}
